import * as React from 'react';
import { useState, useEffect } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Card, Col, Row, Space, Typography, Dropdown, message } from 'antd';

import { GroupBox } from './GroupBox';
import { copyToClipboard } from '../../../../helpers/utils/safeClipboard';
import { adsRulesApi } from '../../../../shared/common/api/endpoints/adsRulesApi';
import { RuleBlock, CampaignRuleTestResponse, CampaignRuleTestRequest, AdSource, HierarchicalActionTypes, TelegramUser } from '../../types';
import { actionHuman } from '../../utils/actionHelpers';
import { uid, summarizeBlock } from '../../utils/helpers';
import { ActionsPanel } from '../actions/ActionsPanel';

interface RuleBlockCardProps {
  block: RuleBlock;
  onChange: (b: RuleBlock) => void;
  scopeOne: 'campaigns' | 'adGroups' | 'ads';
  adSource?: AdSource;
  actionTypes?: HierarchicalActionTypes;
  telegramUsers?: TelegramUser[];
  formData?: {
    adSource?: string;
    accounts?: string[];
    campaignIds?: string[];
    adGroupIds?: string[];
    adIds?: string[];
    scope?: string[];
  };
}

export function RuleBlockCard({ block, onChange, scopeOne, adSource, actionTypes, telegramUsers = [], formData }: RuleBlockCardProps) {
  const [showResult, setShowResult] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testResults, setTestResults] = useState<CampaignRuleTestResponse['data'] | null>(null);

  useEffect(() => {
    setShowResult(false);
    setTestResults(null);
  }, [block.groups]);

  useEffect(() => {
    setShowResult(false);
    setTestResults(null);
  }, [formData?.adSource, formData?.accounts, formData?.campaignIds, formData?.adGroupIds, formData?.adIds, formData?.scope]);

  useEffect(() => {
    setShowResult(false);
    setTestResults(null);
  }, [block.actions.budgetAction, block.actions.budgetAmount, block.actions.budgetUnit, block.actions.actionType]);

  const addGroup = () =>
    onChange({
      ...block,
      groups: [
        ...block.groups,
        {
          id: uid(),
          logic: 'OR',
          periodDays: block.periodDays,
          atoms: [{
            id: uid(),
            metric: 'user_price',
            comparator: '<',
            value: '',
            periodDays: 0
          }]
        },
      ],
    });

  // Auto-fill Telegram message
  useEffect(() => {
    if (!block.actions.sendTelegram) return;

    const cond = summarizeBlock(block);
    const act = actionHuman(scopeOne, block.actions, actionTypes, adSource);

    // Определяем тип объектов для сообщения
    const getObjectType = () => {
      if (scopeOne === 'campaigns') return 'кампании';
      if (scopeOne === 'adGroups') return 'группы';
      return 'объявления';
    };

    // Получаем номера объектов (пока заглушка, так как в реальности нужны данные из API)
    const getObjectNumbers = () => {
      // В реальном приложении здесь будут реальные ID выбранных объектов
      return '123456, 789012';
    };

    const objectType = getObjectType();
    const objectNumbers = getObjectNumbers();

    const message = `#Управление_рекламой
#{rule_name}
#{account_name}

🚦Сработало условие: {condition}
🔴Сделано: {action_taken}
⬇️По №${objectType}:
{object_numbers}`;

    const current = block.actions.tgMessage || '';
    if (current === '' && current !== message) {
      onChange({ ...block, actions: { ...block.actions, tgMessage: message } });
    }
  }, [block.groups, block.actions.budgetAction, block.actions.budgetAmount, block.actions.budgetUnit, block.actions.sendTelegram, block.actions.actionType, scopeOne, actionTypes, adSource]);

  const getObjectTypeName = () => {
    if (scopeOne === 'campaigns') return 'кампаний';
    if (scopeOne === 'adGroups') return 'групп объявлений';
    return 'объявлений';
  };

  const getMockObjectNumbers = () => {
    if (scopeOne === 'campaigns') {
      return [
        '123456789 - Бренд — Поиск',
        '987654321 - Ремаркетинг — Медийная',
        '456789123 - Конкуренты — Поиск',
        '789123456 - Каталог — Смарт-баннеры',
        '321654987 - Лид-формы — Тест',
        '654987321 - Видео — YouTube',
        '147258369 - Акция — Сентябрь',
        '258369147 - Ретаргетинг — Карточка',
        '369147258 - Промо — Осень',
        '741852963 - Тест — Новый'
      ];
    } else if (scopeOne === 'adGroups') {
      return [
        '201234567 - AG — Бренд / Москва',
        '301234567 - AG — Ремаркетинг / РФ',
        '401234567 - AG — Конкуренты / Поиск',
        '501234567 - AG — Смарт-баннеры / Каталог',
        '601234567 - AG — Лид-формы / Тест',
        '701234567 - AG — Видео / YouTube',
        '801234567 - AG — Акция / Сентябрь',
        '901234567 - AG — Ретаргетинг / Карточка',
        '111234567 - AG — Промо / Осень',
        '121234567 - AG — Тест / Новый'
      ];
    } else {
      return [
        '301234567 - Текстово-графическое #1',
        '302234567 - Баннер 300x250 — A/B',
        '303234567 - Видео 15с — Brand',
        '304234567 - Смарт-объявление — Товары',
        '305234567 - Текстовое — Акция',
        '306234567 - Графическое — Промо',
        '307234567 - Видео 30с — Тест',
        '308234567 - Баннер 728x90 — Новый',
        '309234567 - Смарт — Каталог',
        '310234567 - Текст — Финал'
      ];
    }
  };

  const objectNumbers = getMockObjectNumbers();

  const handleCopyNumber = async (item: string) => {
    const number = item.split(' - ')[0]; // Извлекаем только номер
    const success = await copyToClipboard(number);
    if (success) {
      message.success(`Номер ${number} скопирован в буфер обмена`);
    } else {
      message.error('Не удалось скопировать номер');
    }
  };

  const dropdownItems = objectNumbers.map((item, index) => ({
    key: index.toString(),
    label: (
      <div
        style={{
          padding: '4px 8px',
          fontSize: '12px',
          cursor: 'pointer',
          borderRadius: '4px',
          transition: 'background-color 0.2s'
        }}
        title="Нажмите, чтобы скопировать номер"
        onClick={(e) => {
          e.stopPropagation();
          handleCopyNumber(item);
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f0f0f0';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {item}
      </div>
    ),
  }));

  const handleShowResult = async () => {
    if (!formData) {
      message.error('Необходимо заполнить данные формы');
      return;
    }
    if (!formData.accounts || formData.accounts.length === 0) {
      message.error('Необходимо выбрать рекламный кабинет');
      return;
    }

    try {
      setTestLoading(true);

      const sourceId = formData.adSource === 'yandex_direct' ? 2 : 9;
      const objectTypeMap = {
        'campaigns': 1,
        'adGroups': 2,
        'ads': 3
      } as const;
      const objectTypeId = objectTypeMap[formData.scope?.[0] as 'campaigns' | 'adGroups' | 'ads' || 'campaigns'] as 1 | 2 | 3;

      let actionTypeId: number;
      if (block.actions.actionType && !isNaN(Number(block.actions.actionType))) {
        actionTypeId = parseInt(block.actions.actionType.toString());
      } else {
        const actionTypeMap = {
          'none': 1,
          'increase': 2,
          'decrease': 3,
          'disable': 4
        } as const;
        actionTypeId = actionTypeMap[block.actions.budgetAction || 'none'] as 1 | 2 | 3 | 4;
      }

      const actionUnitId = block.actions.budgetUnit === 'rub' ? 1 : 2;

      // Валидация threshold_value перед созданием condition_blocks
      for (const group of block.groups) {
        for (const atom of group.atoms) {
          if (Number(atom.value) === 0) {
            const periodDays = atom.periodDays ?? block.periodDays;
            message.error(`Значение за последние ${periodDays} дней не может быть 0`);
            setTestLoading(false);
            return;
          }
        }
      }

      const conditionBlocks = block.groups.map(group => ({
        conditions: group.atoms.map(atom => {
          const base = {
            metric_name: atom.metric,
            comparison_operator: atom.comparator,
            threshold_value: Number(atom.value)
          } as any;

          const start = atom.periodDays ?? block.periodDays;
          const end = typeof atom.periodDaysUntil === 'number' ? atom.periodDaysUntil : undefined;

          base.period_days_start = start;
          base.period_days_end = (typeof end === 'number' && end > 0) ? end : 0;

          return base;
        })
      }));

      const adAccounts = (formData.accounts || []).map(id => {
        if (typeof id === 'number') return id;
        if (typeof id === 'string' && id.startsWith('acc-')) {
          return parseInt(id.replace('acc-', ''));
        }
        return parseInt(id);
      });

      const adCampaigns = (formData.campaignIds || []).map(id => {
        if (typeof id === 'number') return id;
        if (typeof id === 'string' && id.startsWith('c-')) {
          return parseInt(id.replace('c-', ''));
        }
        return parseInt(id);
      });

      const adGroups = (formData.adGroupIds || []).map(id => {
        if (typeof id === 'number') return id;
        if (typeof id === 'string' && id.startsWith('g-')) {
          return parseInt(id.replace('g-', ''));
        }
        return parseInt(id);
      });

      const ads = (formData.adIds || []).map(id => {
        if (typeof id === 'number') return id;
        if (typeof id === 'string' && id.startsWith('a-')) {
          return parseInt(id.replace('a-', ''));
        }
        return parseInt(id);
      });

      const telegramUsers = block.actions.sendTelegram && block.actions.tgRecipients
        ? block.actions.tgRecipients.map(user => parseInt(user.id))
        : null;

      const testData: CampaignRuleTestRequest = {
        rule_name: block.name,
        source_id: sourceId,
        ad_accounts: adAccounts,
        ad_campaigns: adCampaigns,
        ad_groups: adGroups,
        ads: ads,
        object_type_id: objectTypeId,
        criterion_text: block.groups.map(group =>
          group.atoms.map(atom =>
            `${atom.metric} ${atom.comparator} ${atom.value} за последние ${atom.periodDays ?? block.periodDays} дней`
          ).join(' ИЛИ ')
        ).join(' И '),
        action_type_id: actionTypeId as 1 | 2 | 3 | 4,
        action_amount: (actionTypeId === 2 || actionTypeId === 3 || (actionTypeId >= 21 && actionTypeId <= 35)) ? block.actions.budgetAmount : null,
        action_unit_id: (actionTypeId === 2 || actionTypeId === 3 || (actionTypeId >= 21 && actionTypeId <= 35)) ? actionUnitId : null,
        send_telegram: block.actions.sendTelegram,
        telegram_message: block.actions.sendTelegram ? block.actions.tgMessage : null,
        telegram_users: telegramUsers,
        frequency_days: block.periodDays,
        condition_blocks: conditionBlocks
      };

      const response = await adsRulesApi.testRule(testData);

      if (response.data.success) {
        setTestResults(response.data.data);
        setShowResult(true);
        message.success(`Тестирование завершено! Найдено объектов: ${response.data.data.total_matched}`);
      } else {
        message.error('Ошибка при тестировании правила: ' + response.data.message);
      }
    } catch (error: any) {
      console.error('Error testing rule:', error);

      if (error.response?.status === 400) {
        message.error('Неверный формат данных. Проверьте заполнение всех полей.');
      } else if (error.response?.status === 401) {
        message.error('Ошибка авторизации.');
      } else if (error.response?.status === 403) {
        message.error('Недостаточно прав для тестирования правила.');
      } else if (error.response?.status === 422) {
        const errorMessage = error.response?.data?.message || 'Ошибка валидации данных';
        message.error(errorMessage);
      } else {
        message.error('Произошла ошибка при тестировании правила.');
      }
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Row gutter={[16, 16]}>
        <Col span={11}>
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {block.groups.filter((_, index) => index % 2 === 0).map((g) => (
              <GroupBox
                key={g.id}
                group={g}
                onChange={(next) => onChange({ ...block, groups: block.groups.map((x) => (x.id === g.id ? next : x)) })}
                onRemove={() => onChange({ ...block, groups: block.groups.filter((x) => x.id !== g.id) })}
                onAddGroup={addGroup}
                canRemove={block.groups.length > 1}
              />
            ))}
          </Space>
        </Col>
        <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {block.groups.length > 1 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>И</Typography.Text>
          )}
        </Col>
        <Col span={11}>
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {block.groups.filter((_, index) => index % 2 === 1).map((g) => (
              <GroupBox
                key={g.id}
                group={g}
                onChange={(next) => onChange({ ...block, groups: block.groups.map((x) => (x.id === g.id ? next : x)) })}
                onRemove={() => onChange({ ...block, groups: block.groups.filter((x) => x.id !== g.id) })}
                onAddGroup={addGroup}
                canRemove={block.groups.length > 1}
              />
            ))}
          </Space>
        </Col>
      </Row>

      <Card size="small" style={{ background: '#fafafa' }}>
        <div style={{ marginBottom: 6 }}>
          <strong>Резюме условий</strong>
        </div>
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {summarizeBlock(block)}
        </div>
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-start' }}>
          {showResult && testResults ? (
            <Typography.Text>
              Найдено:
              <Dropdown
                menu={{
                  items: testResults.matched_items.map((item, idx) => ({
                    key: idx,
                    label: `${item.id} - ${item.name}`
                  })),
                  style: { maxHeight: '300px', overflowY: 'auto' }
                }}
                trigger={['click']}
                placement="bottomLeft"
              >
                <Typography.Link
                  style={{ marginLeft: 4, marginRight: 4 }}
                  onClick={(e) => e.preventDefault()}
                >
                  {testResults.total_matched} {getObjectTypeName()}
                </Typography.Link>
              </Dropdown>
            </Typography.Text>
          ) : (
            <Button
              type="default"
              onClick={handleShowResult}
              loading={testLoading}
            >
              Показать результат
            </Button>
          )}
        </div>
      </Card>

      <ActionsPanel
        value={block.actions}
        onChange={(v) => onChange({ ...block, actions: v })}
        scopeOne={scopeOne}
        adSource={adSource}
        actionTypes={actionTypes}
        telegramUsers={telegramUsers}
      />
    </Space>
  );
}
